<script setup lang="ts">
import Button from '@components/buttons/button.vue';
import Input from '@components/form/input.vue';
import CenterChildrenWrapper from '@components/layout-wrapper/center-children-wrapper.vue';
import VerticalAlignChildrenWrapper from '@components/layout-wrapper/vertical-align-children-wrapper.vue';
import { useSingleRefComposable } from '@src/application/composables/single-ref-composable.ts';
import { closeLoader, openLoader } from '@utils/loader-util.ts';
import { ref } from 'vue';
import { ClusterMinimal } from '../../cluster/interfaces/cluster-minimal.ts';
import PicklistCluster from '@src/pick/cluster-picklist/picklist-cluster.vue';
import { Picked } from '@src/pick/cluster-picklist/picked.ts';
import Separator from '@components/separator.vue';
import Expandable from '@components/expandable.vue';
import Accordion from '@components/accordion.vue';
import { PicklistLine } from '@src/pick/cluster-picklist/picklist-line.ts';
import { PickCluster } from '@src/pick/cluster-picklist/pick-cluster.ts';
import { useArrayRefComposable } from '@src/application/composables/array-ref-composable.ts';
import ScannerInput from '@components/form/scanner-input.vue';

const { loadClusters, loadPicklistLines, onPick, onPickSerial, onNotFound } = defineProps<{
    loadClusters: () => Promise<Array<PickCluster>>,
    loadPicklistLines: (cluster: ClusterMinimal|null) => Promise<Array<PicklistLine>>,
    enableAbort?: boolean
    enableFinish?: boolean
    onPick: (picked: Picked) => Promise<void>|void
    onPickSerial: (serial: string) => Promise<void>|void
    onNotFound: (picklistLine: PicklistLine) => Promise<void>|void
}>();

const emit = defineEmits<
    ((event: 'finish') => void) &
    ((event: 'abort') => void)
>();

const clusters = ref(await loadClusters());
const { setSingleTemplateRef: setSerialInputRef, singleRef: serialInputRef } = useSingleRefComposable<InstanceType<typeof Input>>();

async function pickSerial(serial: string) {
    openLoader();
    await onPickSerial(serial);
    clusters.value = await loadClusters();
    closeLoader();
    loadPicklistLinesOnClusters();
    serialInputRef.value?.focus();
}

async function pick(picked: Picked) {
    await onPick(picked);
    clusters.value = await loadClusters();
    loadPicklistLinesOnClusters();
    serialInputRef.value?.focus();
}

async function notFound(picklistLine: PicklistLine) {
    await onNotFound(picklistLine);
    clusters.value = await loadClusters();
}

function abort() {
    emit('abort');
}

function finish() {
    emit('finish');
}

const { setTemplateRef, templateRefs } = useArrayRefComposable<InstanceType<typeof PicklistCluster>>();

function loadPicklistLinesOnClusters() {
    for (const templateRef of templateRefs) {
        templateRef.loadContent();
    }
}
</script>

<template>
    <VerticalAlignChildrenWrapper gap-bottom="1" avoid-outer-gap :class="$style.express_pick" :spread="false">
        <template v-if="clusters.length > 0">
            <ScannerInput :ref="setSerialInputRef" icon="barcode" fixed :model-value="null" @submit="pickSerial"/>
        </template>
        <Accordion enable-toggle>
            <PicklistCluster
                v-for="cluster in clusters"
                :ref="setTemplateRef"
                :key="cluster.cluster?.id"
                :pick-cluster="cluster"
                :load-picklist-lines="loadPicklistLines"
                :show-opened="clusters.length === 1"
                :on-pick="pick"
                :on-not-found="notFound"
                @toggle="serialInputRef?.focus()"
            />
        </Accordion>
        <Separator/>
        <Expandable title="Gepickte Artikel">
            <slot name="picked-products"/>
        </Expandable>
        <Separator :border="false" gap="1"/>
        <CenterChildrenWrapper>
            <Button v-if="enableAbort" @click="abort">Abbrechen</Button>
            <Button v-if="enableFinish" context="primary" @click="finish">Umlagern</Button>
        </CenterChildrenWrapper>
    </VerticalAlignChildrenWrapper>
</template>

<style module lang="scss">
.express_pick {
    width: 80em;
    max-width: 100%;
}
</style>
